import { H2 } from 'app/components/Typography';
import { FlexBox } from '../credential';
import { Box, Button, ButtonGroup, Grid, Menu, MenuItem, Stack, TextField } from '@mui/material';
import { Instagram, Pinterest } from '@mui/icons-material';
import { useState } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import {
  collection,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore';
import useAuth from 'app/hooks/useAuth';
import Loading from 'app/components/MatxLoading';
import PostCard from './PostCaard';
import { useRecoilValue } from 'recoil';
import { userInstagramStateAtom, userPinterestStateAtom } from './atom';
import usePinsta from './hooks/usePinsta';

const SocialImageHistory = () => {
  const [selectedSocial, setSelectedSocial] = useState('all');
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastDocRef = useRef(null);
  const [page, setPage] = useState(1);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const pinterests = useRecoilValue(userPinterestStateAtom);
  const instagrams = useRecoilValue(userInstagramStateAtom);
  const { user } = useAuth();
  const [selectedAccount, setSelectedAccount] = useState('all');
  usePinsta();

  useEffect(() => {
    let filtered = [...data];
    if (selectedSocial === 'all') {
      filtered = data;
    } else {
      filtered = data.filter((item) => item.type === selectedSocial);
    }

    if (selectedAccount !== 'all') {
      filtered = filtered.filter((item) => {
        if (item.type === 'instagram_posts') {
          return item.instagramId === selectedAccount;
        } else if (item.type === 'pinterest_pins') {
          return item.pinterestId === selectedAccount;
        }
        return false;
      });
    }

    setFilteredData(filtered);
  }, [selectedSocial, selectedAccount, data]);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const c = collection(getFirestore(), 'social_posts');
        let q = query(c, orderBy('timestamp', 'desc'), limit(10));
        q = query(q, where('uid', '==', user.uid));
        if (lastDocRef.current) {
          q = query(q, startAfter(lastDocRef.current));
        }

        const snapshot = await getDocs(q);
        const newData = snapshot.docs.map((doc) => ({ doc_id: doc.id, ...doc.data() }));
        lastDocRef.current = snapshot.docs[snapshot.docs.length - 1];
        setData((prevData) => [...prevData, ...newData]);
        if (snapshot.docs.length < 10) {
          setCanLoadMore(false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [page, user]);

  return (
    <>
      <Stack direction="row" justifyContent="space-between" mb={2} spacing={2} alignItems="center">
        <H2>Social Posts</H2>

        <Stack direction="row" spacing={2}>
          <TextField
            select
            style={{ minWidth: '200px' }}
            size="small"
            label="Filter by Account"
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            {pinterests.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                <Stack direction="row" spacing={1} gap={1} alignItems="center">
                  <Pinterest /> {account.name}
                </Stack>
              </MenuItem>
            ))}

            {instagrams.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                <Stack direction="row" spacing={1} gap={1} alignItems="center">
                  <Instagram /> {account.name}
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          <ButtonGroup>
            <Button
              variant={selectedSocial === 'all' ? 'contained' : 'outlined'}
              onClick={() => setSelectedSocial('all')}
            >
              All
            </Button>
            <Button
              startIcon={<Instagram />}
              variant={selectedSocial === 'instagram_posts' ? 'contained' : 'outlined'}
              onClick={() => setSelectedSocial('instagram_posts')}
            >
              Reels
            </Button>
            <Button
              startIcon={<Pinterest />}
              variant={selectedSocial === 'pinterest_pins' ? 'contained' : 'outlined'}
              onClick={() => setSelectedSocial('pinterest_pins')}
            >
              Pins
            </Button>
          </ButtonGroup>
        </Stack>
      </Stack>
      <FlexBox style={{ minHeight: '30vh' }}>
        <Box
          sx={{ padding: '10px 20px' }}
          md={{ padding: '15px 25px' }}
          style={{ maxWidth: '1500px' }}
          width="100%"
        >
          <Grid container spacing={2}>
            {filteredData.map((post) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={post.doc_id}>
                <PostCard initialData={post} />
              </Grid>
            ))}
          </Grid>

          {filteredData.length === 0 && !isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                padding: '20px'
              }}
            >
              <H2>No posts found</H2>
            </Box>
          )}

          {!isLoading && canLoadMore && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                padding: '20px'
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={isLoading}
              >
                Load More
              </Button>
            </Box>
          )}
        </Box>
      </FlexBox>

      {isLoading && (
        <FlexBox>
          <Box
            sx={{ padding: '10px 20px' }}
            md={{ padding: '15px 25px' }}
            style={{ maxWidth: '1500px' }}
          >
            <Loading />
          </Box>
        </FlexBox>
      )}
    </>
  );
};

export default SocialImageHistory;
